import axios, {type AxiosError} from 'axios';
import {ElNotification} from "element-plus";

export const axiosVars = {
    baseURL: import.meta.env.VITE_API_BASE_URL,
    apiKey: import.meta.env.VITE_API_KEY
}
export interface ApiResponse<T> {
    status: string;
    data: T;
}

const api = axios.create({
    baseURL: axiosVars.baseURL, // Get the base URL from env
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 5000,
    headers: {
        'Accept': 'application/json',
        'API-KEY': axiosVars.apiKey,
        'Accept-Language': sessionStorage.getItem('lang') || 'de',
    }
});

let sessionExpiredHandler: (() => void) | undefined;
export function setSessionExpiredHandler(handler: () => void): void {
    sessionExpiredHandler = handler;
}

function isCredentialRequest(url = ''): boolean {
    const path = url.split('?')[0].replace(/\/$/, '');
    return /(?:^|\/)(?:login(?:\/(?:google|apple))?|register|logout|password\/(?:email|change))$/.test(path);
}


api.interceptors.request.use(
    (config) => {
        // Example: Add auth token
        const token = localStorage.getItem('token');
        if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Discard late responses from a previous account before a store can cache them.
api.interceptors.response.use(response => {
    const sent=response.config.headers.Authorization;
    if (typeof sent==='string' && sent.startsWith('Bearer ') && sent.slice(7)!==localStorage.getItem('token')) {
        return Promise.reject(new axios.CanceledError('The session changed.'));
    }
    return response;
}, error => {
    const sent = error.config?.headers?.Authorization;
    if (typeof sent === 'string' && sent.startsWith('Bearer ')) {
        if (sent.slice(7) !== localStorage.getItem('token')) {
            return Promise.reject(new axios.CanceledError('The session changed.'));
        }
        if (error.response?.status === 401 && !isCredentialRequest(error.config?.url)) {
            // Clear synchronously so concurrent 401s cannot expire the session twice.
            localStorage.removeItem('token');
            delete api.defaults.headers.Authorization;
            sessionExpiredHandler?.();
            return Promise.reject(new axios.CanceledError('The session expired.'));
        }
    }
    return Promise.reject(error);
});

export const doError= (error: AxiosError | any)=> {
    // Session recovery already informs the user; stale-account requests are discarded quietly.
    if (axios.isCancel(error)) throw error;
    const e=error.response?.data?.message || 'An error occurred';
    ElNotification({
        type: 'error',
        title: e,
    })
    throw Object.assign(new Error(e), { status: error.response?.status });
}
export default api;
